/**
 * FeedbackPanel - User feedback interface for iterative refinement
 * Integrates with HI-SI's existing iterative system
 */

import React, { useState, useEffect } from 'react';
import { 
  Send, 
  RotateCcw, 
  RefreshCw, 
  CheckCircle,
  AlertTriangle,
  Lightbulb,
  Layers
} from 'lucide-react';
import { StageType, ExecutionStatus } from '@/types/execution';
import { cn } from '@/utils/cn';

// Types for user feedback system
export interface UserFeedbackEvaluation {
  type: 'user_feedback';
  decision: 'improve' | 'rewrite' | 'accept_with_notes';
  feedback: string;
  priority: 'override' | 'supplement';
  scores_override?: Partial<QualityScores>;
  preserve_elements?: string[];
  specific_issues?: string[];
  suggestions?: string[];
  timestamp: string;
  stage: StageType;
  iteration_number: number;
}

export interface QualityScores {
  hook_effectiveness: number;
  narrative_clarity: number;
  visual_coherence: number;
  persona_consistency: number;
  anti_cliche_score: number;
  overall: number;
}

export interface IterationSummary {
  iteration_number: number;
  scores: QualityScores;
  decision: 'accept' | 'improve' | 'rewrite';
  ai_feedback: string;
  persona_used: string;
  is_best_attempt: boolean;
  what_worked: string[];
  what_failed: string[];
  timestamp: string;
}

interface FeedbackPanelProps {
  execution: ExecutionStatus;
  stage: StageType;
  currentIteration: IterationSummary;
  onFeedbackSubmit: (feedback: UserFeedbackEvaluation) => void;
  onCascadePlan?: (feedback: UserFeedbackEvaluation) => void;
  onClose: () => void;
  className?: string;
}

interface FeedbackSuggestions {
  script: string[];
  vision: string[];
  shots: string[];
  storyboard: string[];
  frames: string[];
  continuity: string[];
  videos: string[];
  elements: string[];
  qc: string[];
  assemble: string[];
  music: string[];
  publish: string[];
  music_master: string[];
  music_sidechain: string[];
  export_preview: string[];
}

const FEEDBACK_SUGGESTIONS: FeedbackSuggestions = {
  script: [
    "Make the hook more specific about the target audience's pain point",
    "Add more sensory details to the core exercise or technique",
    "Strengthen the insight with scientific backing or clear mechanism",
    "Reduce repetitive phrasing and vary sentence structure",
    "Make the transformation promise more concrete and measurable"
  ],
  vision: [
    "Replace generic metaphors with industrial/digital/mechanical imagery",
    "Add more structural tension to the transformation narrative",
    "Specify materials, textures, and lighting more clearly",
    "Strengthen the contrast between 'before' and 'after' states",
    "Make the visual progression more architecturally sound"
  ],
  shots: [
    "Adjust pacing - this shot transition feels too fast/slow",
    "Clarify the camera movement's emotional intention",
    "Strengthen visual continuity with the previous shot",
    "Add more specific framing details for impact",
    "Consider the shot's role in building narrative tension"
  ],
  storyboard: [
    "Enhance the visual flow between panels",
    "Add more detail to key transformation moments",
    "Strengthen the visual hierarchy of important elements",
    "Clarify the emotional arc through visual composition",
    "Improve the pacing of reveal moments"
  ],
  frames: [
    "Adjust lighting to better match the intended mood",
    "Refine background elements to support the main subject",
    "Enhance color grading for emotional consistency",
    "Improve composition to guide viewer attention",
    "Add more environmental detail for realism"
  ],
  continuity: [
    "Ensure character appearance consistency across shots",
    "Maintain environmental lighting throughout sequence",
    "Check prop placement and visibility continuity",
    "Verify timeline logic remains clear",
    "Maintain spatial relationships between elements"
  ],
  videos: [
    "Adjust motion intensity to match content energy",
    "Improve transition timing between key moments",
    "Enhance motion to support narrative flow",
    "Refine camera movement for emotional impact",
    "Optimize pacing for engagement and retention"
  ],
  elements: [
    "Enhance visual elements consistency across frames",
    "Improve overlay positioning and timing",
    "Adjust graphic elements for better readability",
    "Optimize element animations for impact",
    "Refine color coordination between elements"
  ],
  qc: [
    "Review audio-visual synchronization accuracy",
    "Check for visual artifacts or quality issues",
    "Verify content guidelines compliance",
    "Validate technical specifications adherence",
    "Confirm overall production quality standards"
  ],
  assemble: [
    "Improve sequence flow and transitions",
    "Optimize segment duration for engagement",
    "Enhance overall narrative coherence",
    "Adjust pacing between different sections",
    "Refine final cut for maximum impact"
  ],
  music: [
    "Adjust music tempo to match content energy",
    "Improve audio mixing and balance",
    "Enhance musical transitions between sections",
    "Optimize volume levels throughout",
    "Better align music with visual beats"
  ],
  publish: [
    "Optimize export settings for target platform",
    "Ensure metadata and tags are accurate",
    "Verify final output quality and compression",
    "Check thumbnail and preview generation",
    "Confirm distribution channel requirements"
  ],
  music_master: [
    "Fine-tune overall audio mastering",
    "Optimize dynamic range for platform",
    "Enhance stereo field and spatial audio",
    "Adjust final EQ for clarity",
    "Perfect audio loudness standards"
  ],
  music_sidechain: [
    "Improve vocal clarity over music",
    "Adjust sidechain compression timing",
    "Optimize music ducking for dialogue",
    "Enhance audio separation clarity",
    "Refine automatic volume adjustments"
  ],
  export_preview: [
    "Optimize preview quality and file size",
    "Improve thumbnail selection accuracy",
    "Enhance preview clip selection",
    "Adjust preview length for engagement",
    "Verify preview represents final content"
  ]
};

const ANTI_PATTERNS = [
  { pattern: /generic|basic|simple/gi, warning: "Generic descriptions often lead to cliché outputs" },
  { pattern: /make it more/gi, warning: "Vague requests. Be specific about what aspect to enhance" },
  { pattern: /better|good|nice/gi, warning: "Subjective terms. Describe the specific improvement needed" },
  { pattern: /like.*but/gi, warning: "Comparison-based feedback can confuse personas" },
  { pattern: /completely different/gi, warning: "Consider 'rewrite' option instead of preserve + change all" }
];

const FeedbackPanel: React.FC<FeedbackPanelProps> = ({
  execution,
  stage,
  currentIteration,
  onFeedbackSubmit,
  onCascadePlan,
  onClose,
  className
}) => {
  const [feedback, setFeedback] = useState('');
  const [decision, setDecision] = useState<'improve' | 'rewrite' | 'accept_with_notes'>('improve');
  const [priority, setPriority] = useState<'override' | 'supplement'>('supplement');
  const [preserveElements] = useState<string[]>(currentIteration?.what_worked || []);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [detectedIssues, setDetectedIssues] = useState<string[]>([]);

  // Load saved feedback from localStorage
  useEffect(() => {
    const savedFeedback = localStorage.getItem(`feedback-draft-${execution.id}-${stage}`);
    if (savedFeedback) {
      try {
        const parsed = JSON.parse(savedFeedback);
        setFeedback(parsed.feedback || '');
        setDecision(parsed.decision || 'improve');
        setPriority(parsed.priority || 'supplement');
      } catch (e) {
        // Invalid saved data, ignore
      }
    }
  }, [execution.id, stage]);

  // Auto-save feedback draft
  useEffect(() => {
    if (feedback.trim()) {
      localStorage.setItem(`feedback-draft-${execution.id}-${stage}`, JSON.stringify({
        feedback,
        decision,
        priority,
        timestamp: Date.now()
      }));
    }
  }, [feedback, decision, priority, execution.id, stage]);

  // Detect anti-patterns in feedback
  useEffect(() => {
    const issues: string[] = [];
    ANTI_PATTERNS.forEach(({ pattern, warning }) => {
      if (pattern.test(feedback)) {
        issues.push(warning);
      }
    });
    setDetectedIssues(issues);
  }, [feedback]);

  const handleSubmit = () => {
    if (!feedback.trim()) return;

    const userFeedback: UserFeedbackEvaluation = {
      type: 'user_feedback',
      decision,
      feedback: feedback.trim(),
      priority,
      preserve_elements: preserveElements,
      specific_issues: detectedIssues,
      suggestions: [],
      timestamp: new Date().toISOString(),
      stage,
      iteration_number: currentIteration?.iteration_number || 0
    };

    onFeedbackSubmit(userFeedback);

    // Clear draft after submit
    localStorage.removeItem(`feedback-draft-${execution.id}-${stage}`);
    setFeedback('');
    onClose();
  };

  const handleCascadePlan = () => {
    if (!feedback.trim()) return;

    const userFeedback: UserFeedbackEvaluation = {
      type: 'user_feedback',
      decision,
      feedback: feedback.trim(),
      priority,
      preserve_elements: preserveElements,
      specific_issues: detectedIssues,
      suggestions: [],
      timestamp: new Date().toISOString(),
      stage,
      iteration_number: currentIteration?.iteration_number || 0
    };

    onCascadePlan?.(userFeedback);
  };

  // Check if cascade planning is available (not on the last stage)
  const STAGE_ORDER: StageType[] = ['script', 'vision', 'storyboard', 'shots', 'continuity', 'frames', 'videos'];
  const currentStageIndex = STAGE_ORDER.indexOf(stage);
  const hasCascadeStages = currentStageIndex < STAGE_ORDER.length - 1;

  const addSuggestion = (suggestion: string) => {
    const currentText = feedback.trim();
    const newText = currentText ? `${currentText}\n\n${suggestion}` : suggestion;
    setFeedback(newText);
  };

  const canImprove = currentIteration?.iteration_number < 3; // Max iterations from HI-SI
  const canRewrite = currentIteration?.iteration_number < 2;
  const qualityThreshold = 85; // From HI-SI system
  const currentScore = currentIteration?.scores?.overall || 0;

  return (
    <div className={cn('bg-black/95 backdrop-blur-xl rounded-2xl border border-cyan-500/20 shadow-2xl', className)}>
      {/* Header - Already styled by ExecutionPortal wrapper */}
      <div className="hidden"></div>

      {/* Current Iteration Summary */}
      <div className="p-6 bg-gradient-to-r from-cyan-500/5 to-purple-500/5 border-b border-white/10">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-mono text-white/70 uppercase tracking-wider">
            {stage.charAt(0).toUpperCase() + stage.slice(1)} Stage • Iteration {currentIteration?.iteration_number || 1}
          </div>
          <div className={cn(
            'px-3 py-1 rounded-lg text-xs font-mono uppercase tracking-wider',
            currentScore >= qualityThreshold 
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
              : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
          )}>
            {Math.round(currentScore)}% Quality
          </div>
        </div>

        {currentIteration?.ai_feedback && (
          <div className="text-sm text-white/60 mb-2">
            <span className="text-cyan-400 font-mono">AI:</span> {currentIteration.ai_feedback}
          </div>
        )}

        {currentIteration?.what_worked?.length > 0 && (
          <div className="text-sm">
            <span className="text-emerald-400 font-mono">Working:</span>
            <span className="text-white/60 ml-2">
              {currentIteration.what_worked.join(', ')}
            </span>
          </div>
        )}
      </div>

      {/* Feedback Options */}
      <div className="p-6 space-y-6">
        <div>
          <label className="block text-sm font-mono text-white/50 uppercase tracking-wider mb-3">
            How should HI-SI use your feedback?
          </label>
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => setDecision('improve')}
              disabled={!canImprove}
              className={cn(
                'p-4 rounded-lg border text-left transition-all duration-300 group',
                decision === 'improve'
                  ? 'border-cyan-500/50 bg-cyan-500/10 shadow-lg shadow-cyan-500/20'
                  : 'border-white/10 bg-black/30 hover:border-cyan-500/30 hover:bg-cyan-500/5',
                !canImprove && 'opacity-30 cursor-not-allowed'
              )}
            >
              <RefreshCw className={cn(
                "w-5 h-5 mb-2 transition-all",
                decision === 'improve' ? 'text-cyan-400' : 'text-white/40 group-hover:text-cyan-400/60'
              )} />
              <div className={cn(
                "text-sm font-medium transition-all",
                decision === 'improve' ? 'text-cyan-400' : 'text-white/70'
              )}>Build on current attempt</div>
            </button>

            <button
              onClick={() => setDecision('rewrite')}
              disabled={!canRewrite}
              className={cn(
                'p-4 rounded-lg border text-left transition-all duration-300 group',
                decision === 'rewrite'
                  ? 'border-amber-500/50 bg-amber-500/10 shadow-lg shadow-amber-500/20'
                  : 'border-white/10 bg-black/30 hover:border-amber-500/30 hover:bg-amber-500/5',
                !canRewrite && 'opacity-30 cursor-not-allowed'
              )}
            >
              <RotateCcw className={cn(
                "w-5 h-5 mb-2 transition-all",
                decision === 'rewrite' ? 'text-amber-400' : 'text-white/40 group-hover:text-amber-400/60'
              )} />
              <div className={cn(
                "text-sm font-medium transition-all",
                decision === 'rewrite' ? 'text-amber-400' : 'text-white/70'
              )}>Start fresh with guidance</div>
            </button>

            <button
              onClick={() => setDecision('accept_with_notes')}
              className={cn(
                'p-4 rounded-lg border text-left transition-all duration-300 group',
                decision === 'accept_with_notes'
                  ? 'border-emerald-500/50 bg-emerald-500/10 shadow-lg shadow-emerald-500/20'
                  : 'border-white/10 bg-black/30 hover:border-emerald-500/30 hover:bg-emerald-500/5'
              )}
            >
              <CheckCircle className={cn(
                "w-5 h-5 mb-2 transition-all",
                decision === 'accept_with_notes' ? 'text-emerald-400' : 'text-white/40 group-hover:text-emerald-400/60'
              )} />
              <div className={cn(
                "text-sm font-medium transition-all",
                decision === 'accept_with_notes' ? 'text-emerald-400' : 'text-white/70'
              )}>Save notes for next stage</div>
            </button>
          </div>
        </div>

        {/* Priority Setting */}
        <div>
          <label className="block text-sm font-mono text-white/50 uppercase tracking-wider mb-3">
            Feedback Priority
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setPriority('supplement')}
              className={cn(
                'p-3 rounded-lg border text-left transition-all duration-300',
                priority === 'supplement'
                  ? 'border-cyan-500/50 bg-cyan-500/10 shadow-lg shadow-cyan-500/20'
                  : 'border-white/10 bg-black/30 hover:border-cyan-500/30 hover:bg-cyan-500/5'
              )}
            >
              <div className={cn(
                "text-sm font-medium",
                priority === 'supplement' ? 'text-cyan-400' : 'text-white/70'
              )}>Add to AI evaluation</div>
            </button>
            <button
              onClick={() => setPriority('override')}
              className={cn(
                'p-3 rounded-lg border text-left transition-all duration-300',
                priority === 'override'
                  ? 'border-purple-500/50 bg-purple-500/10 shadow-lg shadow-purple-500/20'
                  : 'border-white/10 bg-black/30 hover:border-purple-500/30 hover:bg-purple-500/5'
              )}
            >
              <div className={cn(
                "text-sm font-medium",
                priority === 'override' ? 'text-purple-400' : 'text-white/70'
              )}>Replace AI evaluation</div>
            </button>
          </div>
        </div>

        {/* Feedback Input */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-mono text-white/50 uppercase tracking-wider">Your Feedback</label>
            <button
              onClick={() => setShowSuggestions(!showSuggestions)}
              className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center transition-colors"
            >
              <Lightbulb className="w-4 h-4 mr-1" />
              Suggestions
            </button>
          </div>
          
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder={`Provide specific guidance for The ${stage === 'script' ? 'Writer' : stage === 'vision' ? 'Architect' : 'Operator'}...`}
            className="w-full p-4 bg-black/30 border border-cyan-500/20 rounded-lg text-white/80 placeholder-white/30 focus:border-cyan-500/50 focus:bg-black/50 focus:outline-none transition-all min-h-[120px] font-mono text-sm"
            maxLength={2000}
          />
          <div className="flex justify-between text-xs text-white/40 mt-2">
            <span className="font-mono">{feedback.length}/2000</span>
            <span>Feedback will guide the {stage} persona</span>
          </div>
        </div>

        {/* Anti-pattern Warnings */}
        {detectedIssues.length > 0 && (
          <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-amber-400 mb-2">
                  Feedback Suggestions
                </div>
                <ul className="text-sm text-white/60 space-y-1">
                  {detectedIssues.map((issue, i) => (
                    <li key={i}>• {issue}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Smart Suggestions */}
        {showSuggestions && (
          <div className="border border-cyan-500/20 rounded-lg p-4 bg-black/30">
            <div className="text-sm font-mono text-cyan-400 mb-3 uppercase tracking-wider">
              Common {stage} feedback patterns:
            </div>
            <div className="space-y-2">
              {FEEDBACK_SUGGESTIONS[stage]?.map((suggestion: string, i: number) => (
                <button
                  key={i}
                  onClick={() => addSuggestion(suggestion)}
                  className="block w-full text-left text-sm text-white/60 hover:text-cyan-400 hover:bg-cyan-500/10 p-3 rounded-lg transition-all border border-transparent hover:border-cyan-500/20"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Submit Buttons */}
        <div className="pt-4 border-t border-white/10 space-y-4">
          <div className="text-xs text-white/40 font-mono">
            Will trigger {decision === 'accept_with_notes' ? 'no' : 'new'} iteration
            {hasCascadeStages && onCascadePlan && (
              <span> • or plan cascade to downstream stages</span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleSubmit}
              disabled={!feedback.trim()}
              className={cn(
                'flex-1 px-4 py-3 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 uppercase tracking-wider text-sm',
                feedback.trim()
                  ? 'bg-gradient-to-r from-cyan-500/20 to-cyan-500/10 text-cyan-400 border border-cyan-500/50 hover:from-cyan-500/30 hover:to-cyan-500/20 hover:shadow-lg hover:shadow-cyan-500/20'
                  : 'bg-black/20 text-white/20 border border-white/10 cursor-not-allowed'
              )}
            >
              <Send className="w-4 h-4" />
              <span>Single Stage</span>
            </button>

            {hasCascadeStages && onCascadePlan && (
              <button
                onClick={handleCascadePlan}
                disabled={!feedback.trim() || decision === 'accept_with_notes'}
                className={cn(
                  'flex-1 px-4 py-3 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 uppercase tracking-wider text-sm',
                  feedback.trim() && decision !== 'accept_with_notes'
                    ? 'bg-gradient-to-r from-purple-500/20 to-purple-500/10 text-purple-400 border border-purple-500/50 hover:from-purple-500/30 hover:to-purple-500/20 hover:shadow-lg hover:shadow-purple-500/20'
                    : 'bg-black/20 text-white/20 border border-white/10 cursor-not-allowed'
                )}
                title={decision === 'accept_with_notes' ? 'Cascade not available with accept option' : 'Plan cascade re-execution'}
              >
                <Layers className="w-4 h-4" />
                <span>Plan Cascade</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPanel;